import React, {useEffect, useRef} from 'react';
import {useTranslation} from 'react-i18next';
import {Dialog, DialogContent} from '@taopix/taopix-design-system';
import WaitingDialogItem from './WaitingDialogItem';
import {ReactComponent as WaitingPicture1} from './WaitingPicture1.svg';
import {ReactComponent as WaitingPicture2} from './WaitingPicture2.svg';
import {ReactComponent as WaitingPicture3} from './WaitingPicture3.svg';
import {ReactComponent as WaitingPicture4} from './WaitingPicture4.svg';
import ProgressBar from './ProgressBar';
import {useUiState} from '../../../Context/UserInterface/UserInterfaceContext';
import './DialogSlider.css';

export enum ProgressState {
  NONE,
  PROCESSING,
  COMPLETE,
  FORCECOMPLETE,
}

interface WaitingDialogItemInfo {
  label: string;
  percentage: number;
}

type WaitingDialogProps = {
  open: boolean;
  useAllPictures: boolean;
  uploadProgress: number;
};

const WaitingDialog = ({open, uploadProgress, useAllPictures}: WaitingDialogProps) => {
  const previousProgress = useRef([0]);
  const {t} = useTranslation();
  const bestCropPercentageCap = 80;
  const ui = useUiState();

  useEffect(() => {
    if (open) {
      // Reset on open.
      previousProgress.current = [0];
    }
  }, [open]);

  const items: WaitingDialogItemInfo[] = [
    {label: t('str_TitleUploadingYourPictures'), percentage: 20},
    {label: t('str_TitleFindingEventsAndStories'), percentage: 40},
    {label: t('str_TitleDetectingFaces'), percentage: 60},
  ];

  if (!useAllPictures) {
    items.push({label: t('str_TitleChosingBestCrops'), percentage: bestCropPercentageCap});
    items.push({label: t('str_TitleSelectingBestPictures'), percentage: 100});
  } else {
    items.push({label: t('str_TitleChosingBestCrops'), percentage: 100});
  }

  const getItems = () => {
    // Assume the initial state is done
    let previousState: ProgressState = ProgressState.COMPLETE;

    return items
      .sort((itemA: WaitingDialogItemInfo, itemB: WaitingDialogItemInfo) =>
        itemA.percentage < itemB.percentage ? -1 : itemA.percentage === itemB.percentage ? 0 : 1
      )
      .map((item: WaitingDialogItemInfo, index: number) => {
        let itemState = ProgressState.NONE;

        // Update the previous progress values list.
        if (
          uploadProgress > previousProgress.current[previousProgress.current.length - 1] &&
          uploadProgress !== previousProgress.current[previousProgress.current.length - 1]
        ) {
          previousProgress.current.push(uploadProgress);
        }

        if (uploadProgress >= item.percentage) {
          // If we've gone past this item's percentage, mark it as complete, or force complete if we've reached 100%.
          itemState = 100 === uploadProgress ? ProgressState.FORCECOMPLETE : ProgressState.COMPLETE;
        } else if (ProgressState.COMPLETE === previousState) {
          // If we haven't reached the percentage for this item yet, but the last item is complete, we must be processing this one.
          itemState = ProgressState.PROCESSING;
        }

        previousState = itemState;

        return (
          <WaitingDialogItem
            key={`waitingdialog-item-${index}-${uploadProgress}`}
            label={item.label}
            state={itemState}
          />
        );
      });
  };

  if (!open) return <></>;

  return (
    <Dialog
      open={open}
      width={ui.state.isSmallScreen ? undefined : 600}
      theme={'tpx-theme-dialogs'}
      themeTip="Dialogs"
      title={t('str_TitleAnalysingYourPictures')}
      aria-live="assertive"
    >
      <DialogContent>
        <div className={'flex lg:flex-wrap sm:flex-col sm:space-y-lg'}>
          <div className="dialog-slider w-[250px] overflow-hidden lg:mb-lg">
            <div className="dialog-slider__slides dialog-slider__slides--animating">
              <WaitingPicture1 />
              <WaitingPicture2 />
              <WaitingPicture3 />
              <WaitingPicture4 />
            </div>
          </div>
          <ul className={'lg:ml-lg'}>{getItems()}</ul>
          <ProgressBar progress={uploadProgress} animate={uploadProgress < 100} />
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default WaitingDialog;
